import classNames from "classnames";
import React, { useState } from "react";

import {
  authController,
  ChildAuthComponentProps,
} from "../../../../application/auth";
import ErrorContainer from "../../errorContainer";
import ThrottleButton from "../../throttleButton";

const PasswordlessOTPForm: React.FC<ChildAuthComponentProps> = ({
  useViewState,
  useErrorState,
  useEmailState,
}) => {
  const [otpCode, setOTPCode] = useState<string | undefined>();
  const [, setViewState] = useViewState;
  const [, setErrorMessage] = useErrorState;
  const [email] = useEmailState;
  const [consumeCodeAttemptsLeft, setConsumeCodeAttemptsLeft] =
    useState<number>(10);
  const [resendAttempts, setResendAttempts] = useState<number>(5);
  const [otpCodeError, setOtpCodeError] = React.useState<string>("");
  const [isEnterThrottled, setIsEnterThrottled] =
    React.useState<boolean>(false);
  const throttleTimeInMs = 1000;

  const consumeCodeSubmit = async (event: React.FormEvent) => {
    try {
      event.preventDefault();

      setConsumeCodeAttemptsLeft(consumeCodeAttemptsLeft - 1);

      authController.validateResendAttempts(consumeCodeAttemptsLeft);

      await authController.consumeCode(otpCode);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
        
        if (error.cause) {
          const newConsumeCodeAttemptsLeft = (
            error.cause as { newConsumeCodeAttemptsLeft: number }
          ).newConsumeCodeAttemptsLeft;
          if (newConsumeCodeAttemptsLeft !== undefined) {
            setConsumeCodeAttemptsLeft(newConsumeCodeAttemptsLeft);
          }
        }
      } else {
        setConsumeCodeAttemptsLeft(consumeCodeAttemptsLeft - 1);
        setErrorMessage(error as string);
      }
    }
  };

  const handleResendCode = async (event: React.FormEvent) => {
    try {
      event.preventDefault();

      if (!consumeCodeAttemptsLeft) {
        setViewState("email");
        return;
      }

      authController.validateResendAttempts(resendAttempts);

      await authController.resendCode(email);

      setResendAttempts(resendAttempts - 1);
    } catch (error: unknown) {
      if (error instanceof Error) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage(error as string);
      }
    }
  };

  function onChangeOTPCode(event: React.ChangeEvent<HTMLInputElement>) {
    if (!event.target.value) {
      setOtpCodeError("OTP code is required");
    } else {
      setOtpCodeError("");
    }

    setOTPCode(event.target.value);
  }

  function handleKeyPress(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key == "Enter") {
      event.preventDefault();

      if (!isEnterThrottled) {
        setIsEnterThrottled(true);
        consumeCodeSubmit(event);
        setTimeout(() => setIsEnterThrottled(false), throttleTimeInMs);
      }
    }
  }

  return (
    <>
      <h2 className="auth-header">A code has been emailed to you</h2>
      <div className="auth-form-container">
        <form className="auth-form">
          <div className="field">
            <label htmlFor="consumeCode" className="label">
              Enter code:
            </label>
            <input
              id="consumeCode"
              type="text"
              className="input"
              value={otpCode || ""}
              onKeyDown={handleKeyPress}
              onChange={onChangeOTPCode}
            />
            {otpCodeError && <p className="error-message">{otpCodeError}</p>}
          </div>

          <div className="buttons">
            <ThrottleButton
              throttleTimeInMs={500}
              disabled={!consumeCodeAttemptsLeft || !otpCode}
              classList={classNames("primary-button")}
              type="submit"
              onClick={(event) => consumeCodeSubmit(event)}
            >
              Sign in
            </ThrottleButton>
          </div>
        </form>
        <div className="navigation">
          <button className="button" onClick={() => setViewState("email")}>
            <span className="back-arrow-icon">&larr;</span> Back
          </button>
          <ThrottleButton
            throttleTimeInMs={1000}
            classList="button"
            disabled={!email || !resendAttempts}
            onClick={(event) => handleResendCode(event)}
          >
            Resend email
          </ThrottleButton>
        </div>
        <ErrorContainer useErrorState={useErrorState} />
      </div>
    </>
  );
};

export default PasswordlessOTPForm;
